/**
 * useJobPoller — polls GET /jobs/{jobId} with exponential backoff.
 *
 * Starts polling when jobId is set and uploadState is 'processing'.
 * Stops automatically when status is terminal (completed / failed / cancelled).
 * Cleans up the interval on unmount.
 */

import { useEffect, useRef } from "react";
import { fetchResultFromUrl, getJobStatus } from "../api/endpoints";
import { useConversionStore } from "../store/conversionStore";

const INITIAL_INTERVAL_MS = 1_000;
const MAX_INTERVAL_MS = 10_000;
const BACKOFF_FACTOR = 1.5;

export function useJobPoller() {
  const { currentJobId, uploadState, setDone, setError } = useConversionStore();
  const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentIntervalMs = useRef(INITIAL_INTERVAL_MS);

  useEffect(() => {
    if (uploadState !== "processing" || !currentJobId) {
      return;
    }

    const poll = async () => {
      try {
        const job = await getJobStatus(currentJobId);

        if (job.status === "completed") {
          clearTimeout(intervalRef.current!);
          if (job.result) {
            // Result inline (small / Redis dev path)
            setDone(job.result);
          } else if (job.result_url) {
            // Result offloaded to S3 (too large to inline) — fetch it directly
            try {
              const result = await fetchResultFromUrl(job.result_url);
              setDone(result);
            } catch {
              setError({
                error_code: "RESULT_FETCH_FAILED",
                message:
                  "The conversion finished but the result could not be loaded. " +
                  "It may be very large — try again or convert without embedded images.",
              });
            }
          } else {
            setError({
              error_code: "RESULT_MISSING",
              message: "The conversion finished but no result was returned.",
            });
          }
          return;
        }

        if (job.status === "failed") {
          clearTimeout(intervalRef.current!);
          setError({
            error_code: job.error?.error_code ?? "CONVERSION_FAILED",
            message: job.error?.message ?? "Conversion failed.",
          });
          return;
        }

        if (job.status === "cancelled") {
          clearTimeout(intervalRef.current!);
          setError({ error_code: "JOB_CANCELLED", message: "Job was cancelled." });
          return;
        }

        // Still pending/processing — schedule next poll with backoff
        currentIntervalMs.current = Math.min(
          currentIntervalMs.current * BACKOFF_FACTOR,
          MAX_INTERVAL_MS
        );
        intervalRef.current = setTimeout(poll, currentIntervalMs.current);
      } catch {
        // Network error — retry with backoff rather than surfacing immediately
        currentIntervalMs.current = Math.min(
          currentIntervalMs.current * BACKOFF_FACTOR,
          MAX_INTERVAL_MS
        );
        intervalRef.current = setTimeout(poll, currentIntervalMs.current);
      }
    };

    currentIntervalMs.current = INITIAL_INTERVAL_MS;
    intervalRef.current = setTimeout(poll, INITIAL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
    };
  }, [currentJobId, uploadState, setDone, setError]);
}
